/**
 * Finance → Investors → one investor → Weekly breakdown.
 *
 * One investor: their bikes and who rides them, each week's remittance on
 * those bikes and how it divides, and their money.
 *
 *   Collected for investors  their share of what riders actually paid
 *   Paid to investors        payouts recorded to them
 *   Owed to investors        collected less paid out
 *   Still with riders        their share of charged weeks not yet paid
 *
 * Recording a rider's payment never marks the investor paid: paying them is
 * its own action, never more than has been collected for them.
 */

import { useMemo, useState, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Bike, HandCoins, Link2, Pencil } from 'lucide-react';
import { PAYMENT_METHOD_LABELS, type InvestorPayoutStatus, type InvestorWeeks, type PaymentMethod } from '@cenox/shared';
import { Button, Card, EmptyState, ErrorState, Input, Select, TableSkeleton, cn } from '../../components/ui';
import { GeoScopeFields } from '../../components/GeoScope';
import { useAuth } from '../../lib/auth';
import { money, todayIn } from '../../lib/format';
import { useLocationFilter, useLocationReady } from '../../lib/locationContext';
import { SectionTitle, Stat } from './parts';
import { InvestorFormDialog, PayoutForm } from './InvestorForms';
import { LinkBikeDialog, ManageLinkedBikesDialog } from './InvestorBikeDialogs';
import { CorrectLinkDialog } from './CorrectLinkDialog';
import type { InvestorBikeLink } from '@cenox/shared';
import { useInvestor, useInvestorWeeks, useVoidInvestorPayout } from './investorData';

type Preset = 'week' | 'month' | 'year' | 'custom';

const PAYOUT_TEXT: Record<InvestorPayoutStatus, string> = {
  paid_out: 'Paid out',
  part_paid_out: 'Partly paid out',
  not_paid_out: 'Not paid out',
  nothing_collected: 'Nothing collected',
};
const PAYOUT_TONE: Record<InvestorPayoutStatus, string> = {
  paid_out: 'bg-emerald-50 text-emerald-700',
  part_paid_out: 'bg-amber-50 text-amber-700',
  not_paid_out: 'bg-slate-100 text-slate-700',
  nothing_collected: 'bg-slate-50 text-slate-400',
};
const STATUS_TEXT: Record<string, string> = { paid: 'Paid', part_paid: 'Part paid', unpaid: 'Unpaid', waived: 'Waived' };

function addDays(iso: string, days: number): string {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

/** Weeks ENDING in the range: this week ends within the next six days. */
export function rangeFor(preset: Exclude<Preset, 'custom'>, today: string): { from: string; to: string } {
  const [y, m] = today.split('-');
  if (preset === 'week') return { from: today, to: addDays(today, 6) };
  if (preset === 'month') {
    const last = new Date(Date.UTC(Number(y), Number(m), 0)).getUTCDate();
    return { from: `${y}-${m}-01`, to: `${y}-${m}-${String(last).padStart(2, '0')}` };
  }
  return { from: `${y}-01-01`, to: `${y}-12-31` };
}

export function InvestorDetailPage(): ReactNode {
  const { investorId = '' } = useParams();
  const { business, can } = useAuth();
  const currency = business?.currencyCode ?? 'NGN';
  const today = todayIn(business?.timezone ?? 'Africa/Lagos');
  const detail = useInvestor(investorId || null);
  const voidPayout = useVoidInvestorPayout();
  const [dialog, setDialog] = useState<'edit' | 'link' | 'manage' | null>(null);
  const [paying, setPaying] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const [correcting, setCorrecting] = useState<InvestorBikeLink | null>(null);

  if (!can('remittance:manage')) {
    return (
      <EmptyState
        icon={<HandCoins className="size-8" />}
        title="Investors are for the owner and administrators"
        description="What each investor is owed and has been paid is shown to whoever manages remittance."
      />
    );
  }
  if (detail.isError) {
    return <ErrorState message="Could not load this investor." onRetry={() => void detail.refetch()} />;
  }
  const investor = detail.data;
  if (!investor) return <TableSkeleton rows={4} columns={4} />;

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <Link to={`/finance/investors/${investor.id}`} className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800">
            <ArrowLeft className="size-4" /> Investor profile
          </Link>
          <h1 className="mt-1 text-xl font-semibold text-slate-900">{investor.name}</h1>
          <p className="text-sm text-slate-500">
            {investor.reference ?? 'No investor ID'} · {investor.isActive ? 'Active' : 'Inactive'}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="secondary" icon={<Pencil className="size-4" />} onClick={() => setDialog('edit')}>
            Edit investor
          </Button>
          <Button variant="secondary" icon={<Link2 className="size-4" />} onClick={() => setDialog('link')} disabled={!investor.isActive}>
            Link bike
          </Button>
          <Button variant="secondary" icon={<Bike className="size-4" />} onClick={() => setDialog('manage')}>
            Manage linked bikes
          </Button>
        </div>
      </div>

      {note ? (
        <p className="rounded-md bg-emerald-50 px-3 py-2 text-sm text-emerald-800" role="status">
          {note}
        </p>
      ) : null}

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-5" data-testid="investor-balances">
        <Stat label="Entitlement accrued" value={investor.balances.entitled} currency={currency} hint="Their share of weeks charged, paid or not" />
        <Stat label="Collected for investor" value={investor.balances.collected} currency={currency} hint="Their share of rider payments received" />
        <Stat label="Paid to investor" value={investor.balances.paidOut} currency={currency} hint="Payouts recorded" />
        <Stat label="Owed to investor" value={investor.balances.payable} currency={currency} hint="Collected, not yet paid out" />
        <Stat
          label="Still with riders"
          value={investor.balances.outstandingFromRiders}
          currency={currency}
          hint="Their share of charged weeks not yet paid"
        />
      </div>

      <dl className="grid grid-cols-1 gap-x-4 gap-y-1 rounded-lg border border-slate-200 bg-white p-3 text-sm sm:grid-cols-2" aria-label="Investor details">
        {(
          [
            ['Investor ID', investor.reference],
            ['Phone', investor.phone],
            ['Email', investor.email],
            ['Status', investor.isActive ? 'Active' : 'Inactive'],
          ] as const
        ).map(([k, v]) => (
          <div key={k} className="flex gap-2">
            <dt className="w-24 shrink-0 text-slate-500">{k}</dt>
            <dd className={v ? 'text-slate-900' : 'text-slate-400'}>{v ?? 'Not set'}</dd>
          </div>
        ))}
        {investor.note ? (
          <div className="flex gap-2 sm:col-span-2">
            <dt className="w-24 shrink-0 text-slate-500">Notes</dt>
            <dd className="text-slate-900">{investor.note}</dd>
          </div>
        ) : null}
      </dl>

      <section>
        <SectionTitle>Linked bikes</SectionTitle>
        {investor.links.length === 0 ? (
          <EmptyState
            icon={<Bike className="size-8" />}
            title="No bikes linked"
            description="Link a bike from the Bike Register to give this investor their share of its weekly remittance."
            action={investor.isActive ? <Button onClick={() => setDialog('link')}>Link bike</Button> : undefined}
          />
        ) : (
          <Card>
            <div className="-mx-3 overflow-x-auto px-3">
              <table className="w-full min-w-[760px] text-sm" aria-label="Linked bikes">
                <thead>
                  <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500">
                    <th className="py-2 pr-3 font-medium">Bike</th>
                    <th className="py-2 pr-3 font-medium">Current rider</th>
                    <th className="py-2 pr-3 font-medium">Owned</th>
                    <th className="py-2 pr-3 text-right font-medium">Share a week</th>
                    <th className="py-2 font-medium"><span className="sr-only">Actions</span></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {investor.links.map((l) => (
                    <tr key={l.periodId} className={l.current || l.effectiveTo === null ? '' : 'text-slate-400'}>
                      <td className="py-2 pr-3 font-medium">{l.registration}</td>
                      <td className="py-2 pr-3">
                        {l.current ? (l.riderName ?? 'Not assigned') : '—'}
                        {l.current && l.riderSince ? <span className="block text-xs text-slate-400">since {l.riderSince}</span> : null}
                      </td>
                      <td className="py-2 pr-3">
                        {l.effectiveFrom <= '2000-01-01' ? 'Since registration' : `From ${l.effectiveFrom}`}
                        {l.effectiveTo ? ` to ${l.effectiveTo}` : ''}
                      </td>
                      <td className="py-2 pr-3 text-right tabular-nums">
                        {l.sharePercent ? `${l.sharePercent}% of remittance` : money(l.weeklyShare, currency)}
                        <span className="block text-xs text-slate-400">{l.shareSource === 'agreement' ? 'agreement' : 'default'}</span>
                      </td>
                      <td className="py-2 text-right">
                        {l.current || l.effectiveTo === null ? (
                          <Button size="sm" variant="ghost" onClick={() => setCorrecting(l)}>
                            Edit agreement / Start date
                          </Button>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}
      </section>

      <WeeklyBreakdown investorId={investor.id} currency={currency} today={today} />

      <section>
        <SectionTitle
          aside={
            !paying ? (
              <Button size="sm" icon={<HandCoins className="size-4" />} onClick={() => setPaying(true)} disabled={Number(investor.balances.payable) <= 0}>
                Record payout
              </Button>
            ) : null
          }
        >
          Payouts to the investor
        </SectionTitle>
        {paying ? (
          <PayoutForm
            investorId={investor.id}
            payable={investor.balances.payable}
            currency={currency}
            onDone={() => setPaying(false)}
            onSaved={() => {
              setPaying(false);
              setNote('Payout recorded. Owed to the investor is updated.');
            }}
          />
        ) : null}
        {investor.payouts.length === 0 ? (
          <p className="text-sm text-slate-500">Nothing paid out yet.</p>
        ) : (
          <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200 bg-white" aria-label="Payouts">
            {investor.payouts.map((p) => (
              <li key={p.id} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 text-sm">
                <span className={p.voided ? 'text-slate-400 line-through' : 'font-medium text-slate-900'}>
                  {money(p.amount, currency)} · {p.paidOn}
                </span>
                <span className="text-xs text-slate-500">
                  {PAYMENT_METHOD_LABELS[p.method as PaymentMethod] ?? p.method}
                  {p.accountName ? ` · ${p.accountName}` : ''}
                  {p.reference ? ` · Ref ${p.reference}` : ''}
                  {p.voided ? ` · voided: ${p.voidReason}` : ''}
                </span>
                {!p.voided ? (
                  <Button
                    size="sm"
                    variant="ghost"
                    disabled={voidPayout.isPending}
                    onClick={() => {
                      const reason = window.prompt('Why is this payout being voided?');
                      if (reason && reason.trim()) voidPayout.mutate({ id: p.id, reason: reason.trim() });
                    }}
                  >
                    Void
                  </Button>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </section>

      {dialog === 'edit' ? (
        <InvestorFormDialog
          investor={investor}
          onClose={() => setDialog(null)}
          onSaved={() => {
            setDialog(null);
            setNote('Investor details saved.');
          }}
        />
      ) : null}
      {dialog === 'link' ? (
        <LinkBikeDialog
          investorId={investor.id}
          onClose={() => setDialog(null)}
          onDone={(m) => {
            setDialog(null);
            setNote(m);
          }}
        />
      ) : null}
      {correcting ? (
        <CorrectLinkDialog
          investorId={investor.id}
          investorName={investor.name}
          link={correcting}
          onClose={() => setCorrecting(null)}
          onDone={(m) => {
            setCorrecting(null);
            setNote(m);
          }}
        />
      ) : null}
      {dialog === 'manage' ? (
        <ManageLinkedBikesDialog investor={investor} currency={currency} onClose={() => setDialog(null)} onLink={() => setDialog('link')} />
      ) : null}
    </div>
  );
}

function WeeklyBreakdown({ investorId, currency, today }: { investorId: string; currency: string; today: string }): ReactNode {
  const [preset, setPreset] = useState<Preset>('month');
  const [from, setFrom] = useState(rangeFor('month', today).from);
  const [to, setTo] = useState(rangeFor('month', today).to);
  const scope = useLocationFilter();
  const ready = useLocationReady();
  const range = preset === 'custom' ? { from, to } : rangeFor(preset, today);
  const query = useMemo(() => ({ ...range, ...scope }), [range.from, range.to, scope]); // eslint-disable-line react-hooks/exhaustive-deps
  const weeks = useInvestorWeeks(investorId, query, ready && range.from <= range.to);
  const w = weeks.data;

  return (
    <section>
      <SectionTitle>Weekly breakdown</SectionTitle>
      <Card>
        <div className="mb-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
          <Select
            label="Weeks ending"
            value={preset}
            onChange={(e) => setPreset(e.target.value as Preset)}
            options={[
              { value: 'week', label: 'This week' },
              { value: 'month', label: 'This month' },
              { value: 'year', label: 'This year' },
              { value: 'custom', label: 'Custom dates' },
            ]}
          />
          {preset === 'custom' ? (
            <>
              <Input label="From" type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
              <Input label="To" type="date" value={to} onChange={(e) => setTo(e.target.value)} />
            </>
          ) : null}
          <GeoScopeFields bare idPrefix="investor-weeks-scope" />
        </div>
        <p className="mb-3 text-xs text-slate-500" data-testid="allocation-note">
          Each rider payment is divided in proportion to that week’s agreed split. For example, ₦7,500 paid against a
          ₦15,000 week with a ₦10,000 investor share gives ₦5,000 to the investor and ₦2,500 to the company; the payment
          that completes the week settles the exact remainder. When a bike changes riders midweek the investor’s share
          is divided by the days each rider held it.
        </p>

        {weeks.isError ? (
          <ErrorState message="Could not load the weekly breakdown." onRetry={() => void weeks.refetch()} />
        ) : !w ? (
          <TableSkeleton rows={3} columns={6} />
        ) : w.rows.length === 0 ? (
          <p className="py-4 text-center text-sm text-slate-500">No charged weeks on this investor’s bikes in these dates.</p>
        ) : (
          <WeeksTable weeks={w} currency={currency} />
        )}
      </Card>
    </section>
  );
}

function WeeksTable({ weeks, currency }: { weeks: InvestorWeeks; currency: string }): ReactNode {
  const t = weeks.totals;
  return (
    <>
      {weeks.reviewCount > 0 ? (
        <p className="mb-3 rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-800" role="status">
          {weeks.reviewCount} {weeks.reviewCount === 1 ? 'week needs' : 'weeks need'} a check of who held the bike — see the
          notes below.
        </p>
      ) : null}
      <div className="-mx-3 overflow-x-auto px-3">
        <table className="w-full min-w-[980px] text-sm" aria-label="Weekly breakdown" data-testid="investor-weeks">
          <thead>
            <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500">
              <th className="py-2 pr-3 font-medium">Week</th>
              <th className="py-2 pr-3 font-medium">Bike</th>
              <th className="py-2 pr-3 font-medium">Rider</th>
              <th className="py-2 pr-3 text-right font-medium">Total due</th>
              <th className="py-2 pr-3 text-right font-medium">Paid</th>
              <th className="py-2 pr-3 text-right font-medium">Remaining</th>
              <th className="py-2 pr-3 text-right font-medium">Investor</th>
              <th className="py-2 pr-3 text-right font-medium">Company</th>
              <th className="py-2 font-medium">Payout</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {weeks.rows.map((r) => (
              <tr key={r.remittanceId} className="align-top">
                <td className="py-2 pr-3 whitespace-nowrap">
                  {r.weekStartsOn} – {r.weekEndsOn}
                  <span className="block text-xs text-slate-400">{STATUS_TEXT[r.status] ?? r.status}</span>
                </td>
                <td className="py-2 pr-3 whitespace-nowrap">{r.bikeRegistration ?? '—'}</td>
                <td className="py-2 pr-3">
                  {r.riderName}
                  {r.heldDays !== null && r.sharedDays ? (
                    <span className="block text-xs text-slate-400">
                      held {r.heldDays} of {r.sharedDays} days
                    </span>
                  ) : null}
                  {r.review ? <span className="block max-w-xs text-xs text-amber-700">{r.review}</span> : null}
                </td>
                <td className="py-2 pr-3 text-right tabular-nums">{money(r.amountDue, currency)}</td>
                <td className="py-2 pr-3 text-right tabular-nums">{money(r.collected, currency)}</td>
                <td className="py-2 pr-3 text-right tabular-nums">{money(r.outstanding, currency)}</td>
                <td className="py-2 pr-3 text-right tabular-nums">
                  <span className="font-medium text-slate-900">{money(r.investorCollected, currency)}</span>
                  <span className="block text-xs text-slate-400">of {money(r.investorEntitlement, currency)}</span>
                </td>
                <td className="py-2 pr-3 text-right tabular-nums">
                  {money(r.companyCollected, currency)}
                  <span className="block text-xs text-slate-400">of {money(r.companyEntitlement, currency)}</span>
                </td>
                <td className="py-2">
                  <span className={cn('inline-block rounded px-1.5 py-0.5 text-xs font-medium', PAYOUT_TONE[r.payoutStatus])}>
                    {PAYOUT_TEXT[r.payoutStatus]}
                  </span>
                  {r.payoutStatus === 'part_paid_out' ? (
                    <span className="block text-xs text-slate-400">{money(r.investorPaidOut, currency)} paid</span>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t-2 border-slate-200 font-medium text-slate-900">
              <td className="py-2 pr-3" colSpan={3}>
                Total
              </td>
              <td className="py-2 pr-3 text-right tabular-nums">{money(t.amountDue, currency)}</td>
              <td className="py-2 pr-3 text-right tabular-nums">{money(t.collected, currency)}</td>
              <td className="py-2 pr-3 text-right tabular-nums">{money(t.outstanding, currency)}</td>
              <td className="py-2 pr-3 text-right tabular-nums">
                {money(t.investorCollected, currency)}
                <span className="block text-xs font-normal text-slate-400">of {money(t.investorEntitlement, currency)}</span>
              </td>
              <td className="py-2 pr-3 text-right tabular-nums">
                {money(t.companyCollected, currency)}
                <span className="block text-xs font-normal text-slate-400">of {money(t.companyEntitlement, currency)}</span>
              </td>
              <td className="py-2 text-xs font-normal text-slate-500">{money(t.investorPaidOut, currency)} paid out</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </>
  );
}
